import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { FaUserCircle, FaCamera, FaCheckCircle } from "react-icons/fa";
import "./ProfilePage.css";
import Layout from "./Layout";

export default function ProfilePage() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profilePicture, setProfilePicture] = useState(null);
  const [showToast, setShowToast] = useState(false); // State for the toast popup
  const fileInputRef = useRef(null);

  // Initialize form with real user data for auth fields, and specific dummy data for the rest
  const [formData, setFormData] = useState({
    name: currentUser?.name || "",
    username: currentUser?.username || "",
    email: currentUser?.email || "",
    password: "",
    // Dummy Data Placeholders
    dob: "2001-06-25",
    presentAddress: "Bangalore, Karnataka, India",
    permanentAddress: "Bangalore, Karnataka, India",
    city: "Bangalore",
    postalCode: "560001",
    country: "India",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Please select a valid image file (JPEG, PNG, GIF)");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicture(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = () => {
    console.log("Profile data:", formData);
    if (profilePicture) {
      console.log("Profile picture updated");
    }
    
    // Trigger the toast notification
    setShowToast(true);

    // Hide the toast after 3 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  return (
    <Layout>
      <div className="profile-root">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleProfilePictureChange}
          accept="image/jpeg,image/jpg,image/png,image/gif"
          style={{ display: "none" }}
        />

        <div className="profile-content">
          <main className="profile-body">
            <div className="profile-container">
              <h1 className="profile-title">Profile</h1>

              <div className="profile-content-wrapper">
                <div className="profile-avatar-section">
                  <div className="avatar-container">
                    <div className="profile-avatar">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          alt="Profile"
                          className="profile-image"
                        />
                      ) : (
                        <img
                          src="/api/placeholder/120/120"
                          alt="Profile"
                          className="profile-image"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      )}
                      <FaUserCircle style={{ display: "none" }} />
                    </div>
                    <button
                      className="avatar-edit-btn"
                      onClick={handleCameraClick}
                      title="Change profile picture"
                    >
                      <FaCamera />
                    </button>
                  </div>
                </div>

                <div className="profile-form-section">
                  <form
                    className="profile-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSave();
                    }}
                  >
                    <div className="form-grid">
                      <div className="form-group">
                        <label htmlFor="name">Your Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Your Name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="username">User Name</label>
                        <input
                          type="text"
                          id="username"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="User Name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="email@example.com"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="**********"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="dob">Date of Birth</label>
                        <div className="date-input-wrapper">
                          <input
                            type="date"
                            id="dob"
                            name="dob"
                            value={formData.dob}
                            onChange={handleInputChange}
                            className="form-input date-input"
                          />
                          <div className="date-dropdown-icon">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 8"
                              fill="none"
                            >
                              <path
                                d="M1 1L6 6L11 1"
                                stroke="#9CA3AF"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="presentAddress">Present Address</label>
                        <input
                          type="text"
                          id="presentAddress"
                          name="presentAddress"
                          value={formData.presentAddress}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Bangalore, Karnataka, India"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="permanentAddress">
                          Permanent Address
                        </label>
                        <input
                          type="text"
                          id="permanentAddress"
                          name="permanentAddress"
                          value={formData.permanentAddress}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Bangalore, Karnataka, India"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="city">City</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Bangalore"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="postalCode">Postal Code</label>
                        <input
                          type="text"
                          id="postalCode"
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="560001"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="country">Country</label>
                        <input
                          type="text"
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="India"
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="save-btn">
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </main>
        </div>

        {/* TOAST NOTIFICATION COMPONENT */}
        {showToast && (
          <div className="toast-notification">
            <FaCheckCircle className="toast-icon" />
            <span className="toast-text">Profile saved successfully!</span>
          </div>
        )}
      </div>
    </Layout>
  );
}